// GAVION X3: Settings → Data quality (admins).
import type { dataQualityFr } from "./data_quality.fr";

export const dataQualityEn: typeof dataQualityFr = {
  title: "Data quality",
  // GAVION import duplicates / suspect emails: the menu count (duplicate pairs + suspect emails)
  badge: "%{smart_count} item to review |||| %{smart_count} items to review",
  intro:
    "Merge duplicate leads and complete incomplete records before they reach the call queue.",
  demo_notice:
    "Demo mode: detection and merging are computed locally on the demo data.",
  load_error: "Could not load the data. Try again.",
  tabs: {
    duplicates: "Duplicates",
    fix: "To fix",
    suspect_emails: "Suspect emails", // GAVION suspect emails (89_)
  },
  reasons: {
    phone: "Same phone",
    name_city: "Same name and city",
    website: "Same website",
  },
  duplicates: {
    empty: "No duplicates to review. All clean!",
    count: "%{smart_count} pair to review |||| %{smart_count} pairs to review",
    more: "Only the first %{shown} pairs are shown.",
    compare: "Compare",
    close: "Close",
    survivor: "Record to keep",
    survivor_hint:
      "The other record will be deleted; its calls, notes, contacts, deals, tasks and emails move to the kept record.",
    keep: "Keep “%{name}” (#%{id})",
    field: "Field",
    record: "Record #%{id}",
    pick: "Keep record #%{id}'s value for “%{field}”",
    related: "History",
    related_value:
      "%{calls} calls · %{notes} notes · %{contacts} contacts · %{deals} deals · %{tasks} tasks · %{emails} emails",
    counters_hint:
      "The highest attempt count, the earliest added date and the latest call are kept, tags are combined. “Do not call” and unsubscribes are never lost; values that are not kept are copied into a note.",
    email_suppressed: "unsubscribed",
    phone_suppressed: "do not call",
    merge: "Merge",
    not_duplicate: "Not a duplicate",
    confirm_title: "Merge these two leads?",
    confirm_body:
      "Record “%{loser}” (#%{loser_id}) will be deleted and its whole history moved to “%{survivor}” (#%{survivor_id}). This cannot be undone.",
    confirm: "Merge",
    cancel: "Cancel",
    merged: "Leads merged.",
    dismissed: "Pair dismissed: it will not be suggested again.",
  },
  // GAVION suspect emails (89_): "Suspect emails"
  suspect_emails: {
    title: "Suspect emails (%{count})",
    intro:
      "Company emails that look like a typo or already bounced. Check them before the next send.",
    empty: "No suspect emails. All clean!",
    more: "Only the first %{shown} companies are shown.",
    current: "Current email: %{email}",
    reason_typo: "Likely typo: looks like %{domain} (%{source})",
    sources: {
      contact: "contact",
      website: "website",
    },
    reason_bounced: "An email sent to this address bounced",
    replace: "Replace with %{email}",
    other: "Type another address",
    other_label: "New address for %{name}",
    placeholder: "name@company.com",
    invalid: "Invalid address.",
    save: "Replace",
    dismiss: "Ignore",
    confirm_title: "Replace the email?",
    confirm_body:
      "The email of “%{name}” will change from %{from} to %{to}. The next emails will go to the new address.",
    confirm: "Replace",
    cancel: "Cancel",
    saved: "Email replaced.",
    dismissed: "Email kept: it will not be flagged again unless it changes.",
    dismissed_suppressed:
      "Email kept: no longer flagged here, but sending to it stays blocked until it is changed.",
  },
  fields: {
    name: "Name",
    phone_number: "Phone",
    email: "Email",
    website: "Website",
    address: "Address",
    zipcode: "Postal code",
    city: "City",
    state_abbr: "Province",
    region: "Region",
    country: "Country",
    trade: "Trade",
    segment: "Segment",
    sector: "Sector",
    description: "Description",
    linkedin_url: "LinkedIn",
    estimated_employees: "Employees (estimate)",
    language: "Language",
    assigned_sales_id: "Sales rep",
    prospect_status: "Status",
  },
  fix: {
    kinds: {
      missing_phone: "No phone",
      invalid_phone: "Invalid number",
      missing_trade: "No trade",
      missing_region: "No region",
    },
    kind_label: "Fix type",
    empty: "Nothing to fix here.",
    open: "Open record",
    current: "Current: %{phone}",
    no_phone: "No number",
    new_phone: "New number for %{name}",
    placeholder: "514 555-0123",
    valid: "Valid number: %{phone}",
    invalid: "Invalid number: 10 North American digits expected.",
    save: "Save",
    bad_number: "Mark bad number",
    skip: "Skip",
    saved: "Number saved.",
    saved_do_not_call:
      "Number saved. It is on the “Do not call” list: the lead is now “Do not call”.",
    marked: "Lead marked “bad number”.",
    previous: "Previous",
    next: "Next",
    page: "%{from}–%{to} of %{total}",
  },
  errors: {
    forbidden: "Admins only.",
    lead_locked:
      "A rep is calling this lead right now. Try again in a few minutes.",
    not_found: "This lead no longer exists. The list was refreshed.",
    invalid_pair: "Pick two different leads.",
    invalid_field: "Invalid field choice.",
    invalid_phone: "Invalid number: 10 North American digits expected.",
    phone_taken: "This number already belongs to another lead (#%{id}).",
    unhandled_reference:
      "Cannot merge: data linked to this lead (%{table}) cannot be moved. Contact an admin.",
    unhandled_reference_subscription:
      "Cannot merge: the merged-away lead has a subscription or commissions (paid history is kept). Keep that one instead, or contact an admin.",
    // GAVION import duplicates (88_)
    both_active_subscriptions:
      "Both leads have an active subscription: cancel one before merging.",
    paid_commission_lines:
      "The merged-away lead has commissions already paid: keep that one instead.",
    invalid_email: "Invalid email address.", // GAVION suspect emails (89_)
    unknown: "Something went wrong. Try again.",
  },
};
